/**
 * Marker SVG Factory
 * Generates data-URL SVG markers for photo clusters at different zoom levels.
 */

import type { PhotoCategory } from '../../../../shared/types/project';

/** Shared icon paths (feather-style, 24x24 viewBox) */
export const iconPaths: Record<string, string> = {
  camera: 'M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z M12 17a4 4 0 100-8 4 4 0 000 8z',
  warning: 'M12 2L2 22h20L12 2zM12 16v2m0-6v4',
  check: 'M20 6L9 17l-5-5',
  flag: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7',
  pin: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z M12 13a3 3 0 100-6 3 3 0 000 6z',
  star: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
  info: 'M12 3a9 9 0 100 18 9 9 0 000-18z',
  tool: 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z',
  building: 'M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 012-2h10a2 2 0 012 2v16',
  electrical: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
  plumbing: 'M4 14v-2a4 4 0 014-4h2m10 6v2a4 4 0 01-4 4h-2M14 4l-4 4 4 4M10 20l4-4-4-4',
  hvac: 'M8 16a4 4 0 01-4-4 4 4 0 014-4m8 8a4 4 0 004-4 4 4 0 00-4-4m-4-4v16m-4-4h8',
  structural: 'M2 20h20M4 20V10l8-6 8 6v10M9 20v-4h6v4M12 4v2',
  fire: 'M12 22c4-4 8-7.5 8-12a8 8 0 10-16 0c0 4.5 4 8 8 12zM12 12a2 2 0 100-4 2 2 0 000 4z',
  exterior: 'M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6',
  interior: 'M20 9v11a2 2 0 01-2 2H6a2 2 0 01-2-2V9M9 22V12h6v10M2 10.6L12 2l10 8.6',
  drone: 'M10.5 8.5a1.5 2.5 0 013 0v7a1.5 2.5 0 01-3 0zM10 9.5L6 5.5M14 9.5L18 5.5M10 14.5L6 18.5M14 14.5L18 18.5M6 5.5C4.5 4 3 4.5 3 5.5s1 2 2 1.5M18 5.5c1.5-1.5 3-1 3 0s-1 2-2 1.5M6 18.5c-1.5 1.5-3 1-3 0s1-2 2-1.5M18 18.5c1.5 1.5 3 1 3 0s-1-2-2-1.5',
};

/** Helper: encode SVG string to data URL */
function svgToDataUrl(svg: string): string {
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}

/** Helper: badge SVG fragment */
function badgeSvg(x: number, y: number, count: number): string {
  if (count <= 1) return '';
  const label = count > 99 ? '99+' : String(count);
  return `<circle cx="${x}" cy="${y}" r="9" fill="#4F7CFF" stroke="rgba(0,0,0,0.5)" stroke-width="1"/><text x="${x}" y="${y + 4}" text-anchor="middle" fill="white" font-size="10" font-weight="bold" font-family="sans-serif">${label}</text>`;
}

/**
 * Aggregate marker — large neutral circle with total count, no icon.
 * Used at the most zoomed-out level.
 */
export function createAggregateMarkerSvg(count: number, isSelected: boolean): string {
  const size = isSelected ? 56 : 48;
  const r = size / 2 - 4;
  const cx = size / 2;
  const cy = size / 2;
  const fill = isSelected ? '#4F7CFF' : '#6b7280';
  const outline = isSelected
    ? `<circle cx="${cx}" cy="${cy}" r="${r + 2}" fill="none" stroke="white" stroke-width="3"/>`
    : '';
  const label = count > 999 ? '999+' : String(count);
  const fontSize = label.length > 2 ? 12 : 14;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}" stroke="rgba(0,0,0,0.3)" stroke-width="1"/>${outline}<text x="${cx}" y="${cy + fontSize * 0.35}" text-anchor="middle" fill="white" font-size="${fontSize}" font-weight="bold" font-family="sans-serif">${label}</text></svg>`;
  return svgToDataUrl(svg);
}

/**
 * Category marker — medium circle with category color, icon, and count badge.
 * Used at medium zoom.
 */
export function createCategoryMarkerSvg(
  category: PhotoCategory,
  count: number,
  isSelected: boolean,
): string {
  const size = isSelected ? 48 : 40;
  const r = size / 2 - 4;
  const cx = size / 2;
  const cy = size / 2;
  const color = category.color;
  const iconPath = iconPaths[category.icon] || iconPaths.pin;
  const strokeWidth = isSelected ? 3 : 2;

  const outline = isSelected
    ? `<circle cx="${cx}" cy="${cy}" r="${r + 2}" fill="none" stroke="white" stroke-width="3"/>`
    : '';

  const iconScale = 0.58;
  const iconSize = 24 * iconScale;
  const iconOffset = (size - iconSize) / 2;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${cx}" cy="${cy}" r="${r}" fill="${color}" stroke="rgba(0,0,0,0.3)" stroke-width="1"/>${outline}<g transform="translate(${iconOffset}, ${iconOffset}) scale(${iconScale})"><path d="${iconPath}" fill="none" stroke="white" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/></g>${badgeSvg(size - 8, 8, count)}</svg>`;
  return svgToDataUrl(svg);
}

/**
 * Individual marker — current style (small), category color/icon, photo count badge.
 * Used at close zoom. This is the existing createMarkerIcon logic.
 */
export function createIndividualMarkerSvg(
  category: PhotoCategory,
  count: number,
  isSelected: boolean,
): string {
  const size = isSelected ? 44 : 36;
  const color = category.color;
  const iconPath = iconPaths[category.icon] || iconPaths.pin;
  const strokeWidth = isSelected ? 3 : 2;

  const outline = isSelected
    ? `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="none" stroke="white" stroke-width="3"/>`
    : '';

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 4}" fill="${color}" stroke="rgba(0,0,0,0.3)" stroke-width="1"/>${outline}<g transform="translate(${(size - 16) / 2}, ${(size - 16) / 2}) scale(0.667)"><path d="${iconPath}" fill="none" stroke="white" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/></g>${badgeSvg(size - 8, 8, count)}</svg>`;
  return svgToDataUrl(svg);
}
